import React, { MouseEvent, useState, FC } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import {
  useInput,
  useMultiDivisionMultiSectionDropdown,
  useSalesConceptForms,
  useApisManagersSalesConceptCategoriesUpdate,
  useBoolean,
} from "~/hooks";

import { Button, DragDropProvider } from "~/components/atoms";
import { VerticalProgressLine } from "~/components/atoms";
import {
  LabelWithTextField,
  TabList,
  AddFormFieldButton,
  NameOrderableForm,
} from "~/components/molecules";
import { NoDataDisplay } from "~/components/organisms";
import { AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField } from "~/components/organisms";

import { SalesConceptForm } from "./SalesConceptForm";

import {
  SalesConceptCategoryWithSalesConceptType,
  PublicStatus,
  OptionType,
  MultiValueType,
  SalesConceptTagType,
  EmployeeProfileType,
  EditorStateType,
} from "~/domains";

type PropsType = {
  currentEmployee: EmployeeProfileType;
  selectableSalesConceptTags: SalesConceptTagType[];
  salesConceptCategory: SalesConceptCategoryWithSalesConceptType;
};

export const SalesConceptCategoryWithSalesConceptsForm: FC<PropsType> = ({
  selectableSalesConceptTags,
  salesConceptCategory,
  currentEmployee,
}: PropsType) => {
  const navigate = useNavigate();
  const { mutate: doRequest, isLoading: isUpdating } =
    useApisManagersSalesConceptCategoriesUpdate();
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [{ value: categoryName, onChange: onChangeCategoryName }] = useInput(
    salesConceptCategory.name,
  );
  const {
    isChecked: allBusinessSectionsDistribution,
    handleChange: onChangeAllBusinessSectionsDistribution,
  } = useBoolean(salesConceptCategory.allBusinessSectionsDistribution);

  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee.businessDivisions,
    selectableSections: currentEmployee.businessSections,
    defaultDivisions: salesConceptCategory.businessDivisions,
    defaultSections: salesConceptCategory.businessSections,
  });

  const {
    isSubmitting,
    salesConcepts,
    onChangeTitle,
    onChangeContent,
    saveSalesConcept,
    addSalesConcept,
    removeSalesConcept,
    changeDisplayOrder,
    onChangeTopImage,
    handleTagNameCreate,
    onChangeTags,
    onChangeSetNotReadOnly,
    onChangeSetReadOnly,
    selectableSalesConceptTagOptions,
  } = useSalesConceptForms({
    salesConceptCategoryId: salesConceptCategory.id,
    salesConcepts: salesConceptCategory.salesConcepts,
    selectableSalesConceptTags,
  });

  const handleDraftSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const isConfirm = confirm(
      "作成・編集した「各項目」を「保存」しなければ反映されません。下書き保存を実行してよろしいですか？",
    );
    if (!isConfirm) return;

    handleSubmit(PublicStatus.DRAFTED.id);
  };

  const handlePublishSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const isConfirm = confirm(
      "作成・編集した「各項目」を「保存」しなければ反映されません。配布を実行してよろしいですか？",
    );
    if (!isConfirm) return;

    handleSubmit(PublicStatus.PUBLISHED.id);
  };

  const handleSubmit = (publicStatusId: number) => {
    const body = {
      businessSectionIds: optionSelectedSections.map((section) => section.id),
      name: categoryName,
      publicStatusId,
      allBusinessSectionsDistribution,
    };

    doRequest(
      {
        id: salesConceptCategory.id,
        body,
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          navigate("/managers/sales_concept_categories");
        },
      },
    );
  };

  const isCurrentSalesConcept = selectedTabIndex === 0;
  const savedSalesConcepts = salesConcepts.filter(
    (salesConcept) => !salesConcept.isNew,
  );
  return (
    <>
      <div className="space-y-6">
        <AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField
          allParents={selectableDivisions}
          parentsValue={selectedDivisions}
          parentsOnChange={onDivisionChange}
          parentRequired={true}
          parentLabel="配布先: 部署"
          allChildren={optionSelectableSections}
          childrenValue={optionSelectedSections}
          childrenOnChange={onSectionChange}
          childRequired={true}
          childLabel="配布先: 課"
          allBusinessSectionsDistribution={allBusinessSectionsDistribution}
          onChangeAllBusinessSectionsDistribution={
            onChangeAllBusinessSectionsDistribution
          }
          currentEmployeeRole={currentEmployee.employeeRole}
        />
        <LabelWithTextField
          labelText="名前"
          type="text"
          name="name"
          placeholder="名前"
          required
          value={categoryName}
          onChange={onChangeCategoryName}
        />
        <TabList
          tabTexts={["作成", "並び替え"]}
          onClick={(index: number) => setSelectedTabIndex(index)}
          selectedIndex={selectedTabIndex}
        />
        {isCurrentSalesConcept ? (
          salesConcepts.length ? (
            <VerticalProgressLine>
              {salesConcepts.map((salesConcept) => (
                <SalesConceptForm
                  isSubmitting={isSubmitting}
                  defaultAccordionOpen={Boolean(salesConcept.isNew)}
                  key={salesConcept.id}
                  salesConcept={salesConcept}
                  onChangeSetNotReadOnly={() =>
                    onChangeSetNotReadOnly(salesConcept)
                  }
                  onChangeSetReadOnly={() => onChangeSetReadOnly(salesConcept)}
                  removeSalesConcept={() => removeSalesConcept(salesConcept)}
                  saveSalesConcept={() => saveSalesConcept(salesConcept)}
                  onChangeTitle={(newValue: string) =>
                    onChangeTitle({
                      salesConcept,
                      newValue,
                    })
                  }
                  onChangeContent={(newValue: EditorStateType) =>
                    onChangeContent({
                      salesConcept,
                      newValue,
                    })
                  }
                  onChangeTopImage={(newValue: File | undefined) =>
                    onChangeTopImage({
                      salesConcept,
                      newValue,
                    })
                  }
                  handleTagNameCreate={(newValue: string) =>
                    handleTagNameCreate({
                      salesConcept,
                      newValue,
                    })
                  }
                  onChangeTags={(newValue: MultiValueType<OptionType>) =>
                    onChangeTags({
                      salesConcept,
                      newValue,
                    })
                  }
                  selectableSalesConceptTagOptions={
                    selectableSalesConceptTagOptions
                  }
                />
              ))}
            </VerticalProgressLine>
          ) : (
            <NoDataDisplay message="ブログがありません" />
          )
        ) : savedSalesConcepts.length ? (
          <DragDropProvider>
            <VerticalProgressLine>
              {savedSalesConcepts.map((salesConcept, index) => (
                <NameOrderableForm
                  key={`salesConcept${salesConcept.id}`}
                  name={salesConcept.title}
                  index={index}
                  changeDisplayOrder={(beforeIndex: number, newIndex: number) =>
                    changeDisplayOrder(beforeIndex, newIndex)
                  }
                />
              ))}
            </VerticalProgressLine>
          </DragDropProvider>
        ) : (
          <NoDataDisplay message="保存されたライブラリがありません" />
        )}
        {isCurrentSalesConcept && (
          <AddFormFieldButton onClick={addSalesConcept} />
        )}
        <div className="flex items-center space-x-4 mx-auto">
          <Button
            text="下書き保存"
            color="gray"
            outline
            className="w-full"
            onClick={handleDraftSubmit}
            readonly={isUpdating}
          />
          <Button
            text="配布をする"
            color="primary"
            className="w-full"
            onClick={handlePublishSubmit}
            readonly={isUpdating}
          />
        </div>
      </div>
    </>
  );
};
